import errorReporter from '@app/errorReporter';
import PanelBody from '@app/react-common/PanelBody';
import PanelHeader from '@app/react-common/PanelHeader';
import {
  Button,
  InputSearch,
  TabButtons,
  Divider,
  Flex,
  SemanticColor,
} from '@bb/elements-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteAnnouncement,
  getAnnouncements,
  type AnnouncementType,
} from 'api/AnnouncementController';
import { Icon, Pagination } from 'components';
import { useBreakpoint } from 'hooks';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ES_DELAY } from '../constants';
import AnnouncementList from './AnnouncementList';

const TABS = [
  {
    label: 'Previously Sent',
    value: 'sent',
    icon: <Icon type="undo" height={12} />,
  },
  {
    label: 'Scheduled',
    value: 'scheduled',
    icon: <Icon type="antdClockCircle" height={14} />,
  },
  {
    label: 'Saved Drafts',
    value: 'draft',
    icon: <Icon type="antdSaveOutlined" height={14} />,
  },
];

const AnnouncementListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AnnouncementType>('sent');
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const screen = useBreakpoint();
  const isSimplePagination = screen.xs && !screen.md;

  const newAnnouncementButton = (
    <Link key="new-announcement-btn-link" to="/announcement/new">
      <Button
        type="primary"
        color="green"
        size="small"
        dataCy="new-announcement-button"
        minWidth={10}
      >
        + New Announcement
      </Button>
    </Link>
  );

  // Fetch announcements
  const { data, isFetching } = useQuery({
    queryKey: ['announcements', activeTab, page, searchValue],
    queryFn: () => getAnnouncements(activeTab, page, searchValue),
    onError: fetchError => {
      errorReporter.error('Failed to load announcements:', fetchError);
    },
    keepPreviousData: true,
  });

  // Delete announcement mutation
  const deleteMutation = useMutation({
    mutationFn: (announcementId: string) => deleteAnnouncement(announcementId),
    onSuccess: async () => {
      // Wait for ES delay before refetching
      await new Promise(resolve => setTimeout(resolve, ES_DELAY));
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['announcements', activeTab] });
    },
    onError: deleteError => {
      errorReporter.error('Failed to delete announcement:', deleteError);
    },
  });

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleDeleteAnnouncement =
    (announcementId: string) => async (event: React.MouseEvent) => {
      event.stopPropagation();
      deleteMutation.mutate(announcementId);
    };

  const searchAnnouncements = (newSearchValue: string) => {
    setSearchValue(newSearchValue);
    setPage(1); // Reset to first page when searching
  };

  const announcements = data?.announcements || [];
  const count = data?.count || 0;
  const limit = data?.limit || 5;
  const loading = isFetching || deleteMutation.isPending;

  return (
    <StyledRootContainer className="global--angular">
      <PanelHeader
        title="Brokerage Announcements"
        extra={[newAnnouncementButton]}
      />
      <PanelBody style={{ flexDirection: 'column' }}>
        <TabButtons
          dataCy="announcement-list-tabs"
          options={TABS}
          value={activeTab}
          onChange={e => {
            setActiveTab(e.target.value as AnnouncementType);
            setPage(1);
          }}
        />
        <Divider />
        <StyledFlexContainer wrap="wrap" justify="space-between">
          <InputSearch
            placeholder="Search Announcements"
            allowClear
            onSearch={searchAnnouncements}
            dataCy="announcement-list-search-input-container"
          />
          <StyledPagination
            total={count}
            defaultPageSize={5}
            pageSize={limit}
            current={page}
            onChange={onPageChange}
            disabled={loading}
            showSizeChanger={false}
            showLessItems
            simple={isSimplePagination}
          />
          <AnnouncementList
            deleteAnnouncement={handleDeleteAnnouncement}
            isLoading={loading}
            type={activeTab}
            announcements={announcements}
          />
        </StyledFlexContainer>
      </PanelBody>
    </StyledRootContainer>
  );
};

const StyledRootContainer = styled.div`
  > .content {
    padding-bottom: 0;
  }
`;

const StyledFlexContainer = styled(Flex)`
  max-width: 100%;
  flex: 0 0 100%;
  padding: 0 5% 0 5%;
  > .ant-input-search,
  .ant5-input-search {
    flex: 0 1.15 35%;
    .anticon:not(.anticon-close-circle) {
      color: ${SemanticColor.white};
    }

    @media screen and (max-width: 992px) {
      flex: 0.8 1 50%;
    }
  }
`;

const StyledPagination = styled(Pagination)`
  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next {
    border-radius: 0;
    margin: 0 2px;
  }
`;

export default AnnouncementListPage;
